import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FolderPicker } from '@/components/ivr/FolderPicker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { conferenceRooms } from '@/api/conferenceRooms';
import { quizzes } from '@/api/quizzes';
import { surveys } from '@/api/surveys';
import { formatRef } from '@/lib/formatRef';

export type CourseSectionTargetKind = 'folder' | 'conference' | 'quiz' | 'survey';

interface Props {
  kind: CourseSectionTargetKind;
  value: string | null;
  onChange: (id: string | null) => void;
  customerId?: string | null;
  disabled?: boolean;
}

/**
 * Polymorphic picker for the target of a course section. Course sections
 * point at one of four entity kinds (folder / conference room / quiz /
 * survey); this picker delegates folder selection to the existing
 * <FolderPicker /> and shows a shadcn <Select> with name + ref for the
 * other three kinds.
 *
 * When `kind` changes, the previously-selected target is reset to null
 * so we don't accidentally pass an id of one kind to the section as if
 * it were the new kind.
 */
export function CourseSectionTargetPicker({
  kind, value, onChange, customerId, disabled = false,
}: Props) {
  // Reset value to null when kind changes — a folder id is meaningless
  // as a quiz id and vice versa, so we never want to carry stale state.
  useEffect(() => {
    if (value !== null) onChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  if (kind === 'folder') {
    return <FolderPicker value={value} onChange={onChange} disabled={disabled} customerId={customerId} />;
  }

  return (
    <EntitySelect
      kind={kind}
      value={value}
      onChange={onChange}
      customerId={customerId}
      disabled={disabled}
    />
  );
}

// ── Internal: non-folder kinds use a shadcn <Select> ────────────────

function EntitySelect({
  kind, value, onChange, customerId, disabled,
}: {
  kind: 'conference' | 'quiz' | 'survey';
  value: string | null;
  onChange: (id: string | null) => void;
  customerId?: string | null;
  disabled?: boolean;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['course-section-target', kind, customerId ?? null],
    queryFn: () => fetchEntities(kind, customerId ?? undefined),
  });

  const rows = data ?? [];

  return (
    <Select
      value={value ?? ''}
      onValueChange={(v) => onChange(v || null)}
      disabled={disabled || isLoading}
    >
      <SelectTrigger className="h-9 text-sm">
        <SelectValue placeholder={isLoading ? 'Loading…' : placeholderFor(kind)} />
      </SelectTrigger>
      <SelectContent>
        {isLoading ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">No items yet</div>
        ) : (
          rows.map((r) => (
            <SelectItem key={r.id} value={r.id} className="text-sm">
              <span className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {formatRef(r.ref_number, prefixFor(kind))}
                </span>
                <span>{r.name}</span>
              </span>
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

function placeholderFor(kind: 'conference' | 'quiz' | 'survey'): string {
  if (kind === 'conference') return 'Select a conference room';
  if (kind === 'quiz') return 'Select a quiz';
  return 'Select a survey';
}

function prefixFor(kind: 'conference' | 'quiz' | 'survey'): string {
  if (kind === 'conference') return 'CRM';
  if (kind === 'quiz') return 'QUZ';
  return 'SRV';
}

interface EntityRow {
  id: string;
  name: string;
  ref_number: string;
}

async function fetchEntities(
  kind: 'conference' | 'quiz' | 'survey',
  customerId: string | undefined,
): Promise<EntityRow[]> {
  if (kind === 'conference') {
    const res = await conferenceRooms.list({ customer_id: customerId, limit: 200 });
    return res.rows.map((r) => ({ id: r.id, name: r.name, ref_number: r.ref_number }));
  }
  if (kind === 'quiz') {
    const res = await quizzes.list({ customer_id: customerId, limit: 200 });
    return res.rows.map((r) => ({ id: r.id, name: r.name, ref_number: r.ref_number }));
  }
  const res = await surveys.list({ customer_id: customerId, limit: 200 });
  return res.rows.map((r) => ({ id: r.id, name: r.name, ref_number: r.ref_number }));
}
